import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import StorableStore from 'samples/StorableStore';
import {ASCENDING_SORT, DESCENDING_SORT, SortDirection} from 'ui-library/scrollable/ScrollableTableConstants';
import sortedBy from 'util/sortedBy';
import {
  experimentalConditionValuesToString,
  getUniqueExperimentalConditionSummaries,
} from 'workflows/util/ExperimentalConditionUtil';
import {
  INPUT_TABLE_SORT_BY_TYPE,
  InputTableConfig,
  StageRun,
  StageRunSample,
} from 'workflows/WorkflowConstants';

export const SAMPLE_COLUMN = 'SAMPLE';
export const CONTAINER_COLUMN = 'CONTAINER';
export const STAGE_RUN_COLUMN = 'STAGE_RUN';

export interface InputTableRow {
  stageRun: StageRun;
  sampleId: string | null;
  parent: StageRun | null;
  unfulfilledParents: Array<StageRun> | null;
}

function getRowsForExistingSamples(
  orderedInputSamples: Array<{stageRunId: string; stageRunSampleId: string}>,
  stageRunsById: {[stageRunId: string]: StageRun},
  parentIdsByStageRunId: {[stageRunId: string]: Array<string>}
): Array<InputTableRow> {
  const rows = [];
  // Add a row for each stage run + sample pair from orderedInputSamples
  for (const {stageRunId, stageRunSampleId} of orderedInputSamples) {
    const stageRun = stageRunsById[stageRunId];
    if (stageRun == null) {
      assertAndContinue(false, 'No stage run with the id was found', {stageRunId});
      continue;
    }
    const parentStageRuns = parentIdsByStageRunId[stageRunId].map((parentId) => stageRunsById[parentId]);
    const sampleParents = parentStageRuns.filter((parent) =>
      parent.outputSampleIds.some((outputSampleId) => outputSampleId === stageRunSampleId)
    );
    rows.push({
      stageRun,
      sampleId: stageRunSampleId,
      parent: sampleParents.length > 0 ? sampleParents[0] : null,
      unfulfilledParents: null,
    });
  }

  return rows;
}

function getInputSampleIdsByStageRunId(
  orderedInputSamples: Array<{stageRunId: string; stageRunSampleId: string}>
): {[stageRunId: string]: Array<string>} {
  const inputSampleIdsByStageRunId: {[stageRunId: string]: Array<string>} = {};
  for (const {stageRunId, stageRunSampleId} of orderedInputSamples) {
    // eslint-disable-next-line benchlint/no-in-operator
    if (!(stageRunId in inputSampleIdsByStageRunId)) {
      inputSampleIdsByStageRunId[stageRunId] = [stageRunSampleId];
    } else {
      inputSampleIdsByStageRunId[stageRunId] = [...inputSampleIdsByStageRunId[stageRunId], stageRunSampleId];
    }
  }

  return inputSampleIdsByStageRunId;
}

/**
 * Return a array where each item represents the data needed for rendering a row:
 * {
 *   stageRun, // Required
 *   sampleId, // Optional
 *   parent,   // Optional. If specified - one of the parents of the stage run. If sampleId is
 *             // specified, it is one of the outputs of the parent
 *   unfulfilledParents,  // Optional. If specified, represents the list of parents who have not yet
 *                        // sent samples to this stage run
 * }
 */
export function getInputRowDatas(
  orderedInputSamples: Array<{stageRunId: string; stageRunSampleId: string}>,
  parentIdsByStageRunId: {[stageRunId: string]: Array<string>},
  stageRunsById: {[stageRunId: string]: StageRun},
  stageRuns: Array<StageRun>
): Array<InputTableRow> {
  if (orderedInputSamples == null) {
    assertAndContinue(false, `orderedInputSamples argument is expected.`);
    return [];
  }

  const rows = getRowsForExistingSamples(orderedInputSamples, stageRunsById, parentIdsByStageRunId);
  const inputSampleIdsByStageRunId = getInputSampleIdsByStageRunId(orderedInputSamples);

  for (const stageRun of stageRuns) {
    const parentStageRuns = parentIdsByStageRunId[stageRun.id].map((parentId) => stageRunsById[parentId]);
    const inputSampleIds = inputSampleIdsByStageRunId[stageRun.id] || [];
    const unfulfilledParents = parentStageRuns.filter((parentStageRun) =>
      parentStageRun.outputSampleIds.every((outputSampleId) => !inputSampleIds.includes(outputSampleId))
    );
    // Row for sample pending on parent
    if (unfulfilledParents.length !== 0) {
      rows.push({
        stageRun,
        sampleId: null,
        parent: null,
        unfulfilledParents,
      });
    }
    // Row for searching external sample
    if (inputSampleIds.length === 0 && unfulfilledParents.length === 0) {
      rows.push({
        stageRun,
        sampleId: null,
        parent: null,
        unfulfilledParents,
      });
    }
  }
  return rows;
}

function getContainerName(
  sampleId: string,
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample}
): string {
  const sample = stageRunSampleById[sampleId];
  if (sample == null || sample.containerId == null) {
    return '';
  }
  const container = StorableStore.getContainer(sample.containerId);
  return container?.name ?? '';
}

function getSampleName(
  sampleId: string,
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample}
): string {
  const sample = stageRunSampleById[sampleId];
  return sample != null ? sample.name : '';
}

function getSortValue(
  rowData: InputTableRow,
  columnId: string,
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample}
): string {
  if (columnId === SAMPLE_COLUMN) {
    return rowData.sampleId != null ? getSampleName(rowData.sampleId, stageRunSampleById) : '';
  }
  if (columnId === CONTAINER_COLUMN) {
    return rowData.sampleId != null ? getContainerName(rowData.sampleId, stageRunSampleById) : '';
  }
  if (columnId === STAGE_RUN_COLUMN) {
    return rowData.stageRun.name;
  }
  const experimentalConditionValues = rowData.stageRun.stageFlowNode.experimentalConditionValues.filter(
    (value) => value.experimentalCondition && value.experimentalCondition.id === columnId
  );
  return experimentalConditionValues.length > 0
    ? experimentalConditionValuesToString(experimentalConditionValues)
    : '';
}

export function sortInputRowDatas(
  inputRowDatas: Array<InputTableRow>,
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample},
  {sortColumnId, sortDirection}: {sortColumnId: string | null; sortDirection: SortDirection | null}
): Array<InputTableRow> {
  if (sortColumnId == null || sortDirection == null) {
    return inputRowDatas;
  }

  return sortedBy(inputRowDatas, [
    {
      accessor: (rowData) => getSortValue(rowData, sortColumnId, stageRunSampleById),
      reversed: sortDirection === SortDirection.DESCENDING_SORT,
      naturalSort: true,
    },
  ]);
}

export function getSortStateFromInputTableConfig(
  stageRuns: Array<StageRun>,
  inputTableConfig: InputTableConfig | null
): {sortColumnId: string | null; sortDirection: SortDirection | null} {
  let sortColumnId = null;
  let sortDirection = null;

  if (inputTableConfig == null) {
    return {
      sortColumnId,
      sortDirection,
    };
  }

  switch (inputTableConfig.sortByType) {
    case INPUT_TABLE_SORT_BY_TYPE.SAMPLE: {
      sortColumnId = SAMPLE_COLUMN;
      break;
    }
    case INPUT_TABLE_SORT_BY_TYPE.CONTAINER: {
      sortColumnId = CONTAINER_COLUMN;
      break;
    }
    case INPUT_TABLE_SORT_BY_TYPE.STAGE_RUN: {
      sortColumnId = STAGE_RUN_COLUMN;
      break;
    }
    case INPUT_TABLE_SORT_BY_TYPE.EXPERIMENTAL_CONDITION_VALUE: {
      const experimentalConditions = getUniqueExperimentalConditionSummaries(stageRuns);
      const experimentalConditionId = inputTableConfig.sortByExperimentalConditionId;
      assertAndContinue(
        experimentalConditionId != null,
        'Experimental condition id is expected when sort type is EC'
      );
      const ec = _.findWhere(experimentalConditions, {id: experimentalConditionId});
      if (ec != null) {
        // The experimental condition is used by the stage runs and the column is shown
        sortColumnId = ec.id;
      }
      break;
    }
    case INPUT_TABLE_SORT_BY_TYPE.DEFAULT: {
      break;
    }
    default: {
      assert(false, `Unexpected sortByType ${inputTableConfig.sortByType}`);
      break;
    }
  }

  if (sortColumnId != null) {
    sortDirection = inputTableConfig.isSortReversed ? DESCENDING_SORT : ASCENDING_SORT;
  }

  return {
    sortColumnId,
    sortDirection,
  };
}
